

import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";

import { useLanguage } from "@/components/LanguageContext";

interface PageHeaderProps {
  titleEn: string;
  titleFr: string;
  descriptionEn?: string;
  descriptionFr?: string;
  overlineEn?: string;
  overlineFr?: string;
  animatedGif?: boolean;
}

export function PageHeader({ titleEn, titleFr, descriptionEn, descriptionFr, overlineEn, overlineFr, animatedGif }: PageHeaderProps) {
  const { t } = useLanguage();
  
  return (
    <section className="relative w-full pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden flex items-center justify-center min-h-[40vh]">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0 bg-charcoal">
        {animatedGif ? (
          <img
            src="/images/header-animation-v2.gif"
            alt="AVISIUM Solutions Animated Header"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-80"
          />
        ) : (
          <img
            src="/images/Header.jpg"
            alt="AVISIUM Solutions Group Header"
            className="absolute inset-0 w-full h-full object-cover object-center grayscale-[20%] brightness-75 contrast-125 mix-blend-overlay"
          />
        )}

        {/* Dark elegant gradient overlay for readability and contrast (fixes GIF freeze issue) */}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/95 via-charcoal/80 to-warm-white/90" />
      </div>

      <Container className="relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl mx-auto px-4"
        >
          {overlineEn && overlineFr && (
            <span className="block text-sm font-semibold tracking-widest text-amber uppercase mb-4">
              {t(overlineFr, overlineEn)}
            </span>
          )}
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-display font-bold text-warm-white drop-shadow-lg mb-6 leading-tight text-balance">
            {t(titleFr, titleEn)}
          </h1>
          {(descriptionEn || descriptionFr) && (
            <p className="text-lg md:text-2xl text-warm-white/90 font-medium max-w-3xl mx-auto drop-shadow-md leading-relaxed">
              {t(descriptionFr || "", descriptionEn || "")}
            </p>
          )}
        </motion.div>
      </Container>
    </section>
  );
}
